<template>
  <div class="container py-24">
    <h1 class="mb-4 text-4xl font-bold text-foreground">{{ $ts('landing.footer.aboutUs') }}</h1>
    <p class="mb-12 max-w-2xl text-lg text-muted-foreground">
      Bubba is an all-in-one platform that brings pet owners and pet service providers together — making pet care simpler, smarter, and more connected.
    </p>

    <div class="grid gap-12 md:grid-cols-2">
      <!-- For Pet Owners -->
      <div class="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        <div class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon name="tabler:heart" size="24" class="text-primary" />
        </div>
        <h2 class="mb-3 text-2xl font-semibold text-foreground">For Pet Owners</h2>
        <p class="mb-4 text-muted-foreground">
          Everything related to your pet — in one place. Track your pet's health records, book appointments with trusted providers, and stay on top of care routines.
        </p>
        <ul class="space-y-2 text-sm text-muted-foreground">
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Health records, vaccinations, and medication tracking
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Find and book grooming, veterinary, training, and other services
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Manage multiple pets from a single account
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Appointment reminders and care notifications
          </li>
        </ul>
      </div>

      <!-- For Business Partners -->
      <div class="rounded-2xl border border-border/60 bg-card p-8 shadow-sm">
        <div class="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <Icon name="tabler:briefcase" size="24" class="text-primary" />
        </div>
        <h2 class="mb-3 text-2xl font-semibold text-foreground">For Business Partners</h2>
        <p class="mb-4 text-muted-foreground">
          A complete CRM and business management suite built specifically for pet care providers — veterinary clinics, grooming salons, training centres, and more.
        </p>
        <ul class="space-y-2 text-sm text-muted-foreground">
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Online booking, calendar, and appointment management
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Client and pet records in one place
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Team management and staff scheduling
          </li>
          <li class="flex items-start gap-2">
            <Icon name="tabler:check" size="16" class="mt-0.5 shrink-0 text-primary" />
            Business analytics and performance tracking
          </li>
        </ul>
      </div>
    </div>

    <!-- Why Bubba -->
    <div class="mt-16">
      <h2 class="mb-6 text-3xl font-bold text-foreground">Why Bubba?</h2>
      <div class="prose prose-neutral max-w-none dark:prose-invert">
        <p class="text-lg text-muted-foreground">
          Pet care is fragmented — health records on paper, appointments managed by phone, and information scattered across apps. Bubba brings it all together so pet owners and providers have one integrated ecosystem that just works.
        </p>
        <p class="text-lg text-muted-foreground">
          By connecting both sides of pet care on a single platform, everything integrates seamlessly. Providers see their clients' pets and history instantly, owners get a complete view of their pet's care across all providers, and everyone saves time.
        </p>
      </div>
    </div>

    <!-- CTA -->
    <div class="mt-16 rounded-2xl border border-border/60 bg-primary/5 p-8 text-center shadow-sm md:p-12">
      <h2 class="mb-3 text-2xl font-bold text-foreground">Ready to get started?</h2>
      <p class="mx-auto mb-6 max-w-lg text-muted-foreground">
        Whether you're a pet owner looking for the best care or a business ready to grow, Bubba has you covered.
      </p>
      <div class="flex flex-wrap items-center justify-center gap-4">
        <NuxtLink :to="$localeRoute('/download')">
          <Button class="h-12 rounded-full px-8">
            <Icon name="tabler:download" size="18" class="mr-2" />
            Download the App
          </Button>
        </NuxtLink>
        <NuxtLink :to="$localeRoute('/partners')">
          <Button variant="outline" class="h-12 rounded-full px-8">
            Become a Partner
          </Button>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Button from '#bubba/shared/components/ui/button/Button.vue';
import { useI18n, useSchemaOrg, defineWebPage, useSeoMeta } from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';
const { $ts, $localeRoute } = useI18n();

useSeoMeta({
  title: () => $ts('about.seo.title'),
  description: () => $ts('about.seo.description'),
});

useSchemaOrg([
  defineWebPage({ '@type': 'AboutPage' }),
]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'About' },
]);
</script>
